import { Component, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { PageContainerComponent } from '../../layout/page-container/page-container.component';
import { DynamicFormArray, FieldOption, Props } from '../../model/form.model';
import { AseColsnRejectionReportFormArray, AseColsnRejectionReportTableSchema, AseColsnRejectionReportProps, AseColsnRejectionReportTitle, CollisionReportSearchConfig } from '../../model/ase-collision-rejection.model';
import { API_STATUS, ApiEnvNames, ERROR_RESPONSE } from '../../model/api.model';
import { CommonUtils } from '../../utils/common.utils';
import { BaseComponent } from '../base/base.component';
import { DataTableSchema } from '../../model/dynamic-data-table.model';
import { CommonService } from '../../services/common.service';
import { FormGroup } from '@angular/forms';
import { DropdownOptions, FieldNames } from '../../model/form-fields.model';
import { FormService } from '../../services/form.service';
import { AseDropdownSearchConfig } from '../../model/ase-lexus.model';

@Component({
  selector: 'app-collision-rejection-report',
  standalone: true,
  imports: [RouterModule, PageContainerComponent],

  templateUrl: './collision-rejection-report.component.html',
  styleUrl: './collision-rejection-report.component.scss'
})
export class CollisionRejectionReportComponent extends BaseComponent{
  protected title = structuredClone(AseColsnRejectionReportTitle);
  public props: Props = structuredClone(AseColsnRejectionReportProps);  
  protected override data: string = structuredClone(AseColsnRejectionReportTitle);
  protected override filterSchema: DynamicFormArray = structuredClone(AseColsnRejectionReportFormArray);
  public override dataTableSchema: DataTableSchema = structuredClone(AseColsnRejectionReportTableSchema);
  private readonly commonUtils = inject(CommonUtils);
  private readonly service = inject(CommonService);
  formService = inject(FormService);
  lexusForm: FormGroup | undefined;
  public regions:Array<FieldOption> = []

  override ngOnInit() {
    super.ngOnInit();
    super.commonFunction();
    this.props.isrouterGap = true;
    this.props.updatedOn = new Date();
    this.formService.form$.subscribe(form => {
      this.lexusForm = form;
      this.dynamicFieldValidation(form);
    });
  }

  override onSearchClicked = () => {
    this.setTableLoadingState(true);
    this.resetSorting()
    this.dataTableSchema.dataSource = []
    const postParam = structuredClone(CollisionReportSearchConfig);
    this.filterSchema['FieldsArray'].forEach(field=>{
      if(field['selectedValue'] && field['selectedValue'] !== null && field['selectedValue'] !== ''){
        postParam['body'][field['fieldName']] = field['selectedValue']
      }
    });
    this.service.getDataFromDataSource(postParam, ApiEnvNames['AseReport']).then(response => {
      if (response['status'] === API_STATUS['Success']) {
        if (Array.isArray(response.result)) {
          this.dataTableSchema.dataSource = response['result'];
          this.props.updatedOn = response?.result[0] ? response['result'][0].createdOn : '';
        }
      } 
      else {
         this.dataTableSchema.dataSource = [];
         this.commonUtils.showMatSnackBar(ERROR_RESPONSE['errorFetch'])
      }
    }).catch(err => {
      this.dataTableSchema.dataSource = [];
      console.info('Layout Error:- ', err);
    }).finally(() => {
      this.setTableLoadingState(false);
    })
  }

  override updateOptions = async () => {
    const area = this.filterSchema.FieldsArray.find(f => f.fieldName === FieldNames['Area']);
    try {
      const dropDownSearchConfig = structuredClone(AseDropdownSearchConfig);
      const res = await this.service.getDataFromDataSource(dropDownSearchConfig, ApiEnvNames['AseReport']);

      if (res?.status === API_STATUS['Success']) {
        const regions = res.result?.[0]?.toyotaregions || [];
        if (regions.length) {
          this.regions = regions.map((x: { regionCode: any; regionName: any; }) => ({
            fieldValue: x.regionCode,
            fieldLabel: x.regionName
          }));
          const brand = this.filterSchema.FieldsArray.find(f => f.fieldName === FieldNames['Brand']);
          this.applyAreaFieldConfig(area, brand);
        }
      }
    } catch (error) {
      console.info('Error updating options:', error);
    } finally {
      if (area) {
        area.isLoading = false;
      }
    }
  };

  private applyAreaFieldConfig(area: any, brand: any): void {
      if (!area) { return; }
    const brands: string[] = brand?.selectedValue || [];
    if (brands.includes('LEXUS') && brands.includes('TOYOTA')) {
        area.displayName = 'Area / Region';
        area.options = [
          ...DropdownOptions[FieldNames['Area']],
          ...this.regions
        ];
    } else if (brands.includes('TOYOTA')) {
        area.displayName = 'Region';
        area.options = this.regions;
    } else if (brands.includes('LEXUS')) {
        area.displayName = 'Area';
        area.options = DropdownOptions[FieldNames['Area']];
    } else {
        area.displayName = 'Area / Region';
        area.options = [
          ...DropdownOptions[FieldNames['Area']],
          ...this.regions
        ];
    }
  }
  dynamicFieldValidation(form: FormGroup) {
    form.get(FieldNames['Brand'])?.valueChanges.subscribe(value => {
      const area = this.filterSchema.FieldsArray.find(field => field['fieldName'] === FieldNames['Area']);
      if (!area) {return};
      const isLexus = value?.includes('LEXUS');
      const isToyota = value?.includes('TOYOTA');
      if (isLexus && isToyota) {
        area.displayName = 'Area / Region';
        area.options = [
          ...DropdownOptions[FieldNames['Area']],
          ...this.regions
        ];
      } else if (isToyota) {
        area.displayName = 'Region';
        area.options = this.regions;
    } else if (isLexus) {
        area['displayName'] = 'Area';
        area.options = DropdownOptions[FieldNames['Area']];
      } else{
        area['displayName'] = 'Area / Region'
        area['options'] = [...DropdownOptions[FieldNames['Area']], ...structuredClone(this.regions)]
      }
      const selectedValues: string[] = area.selectedValue || [];
      const validValues = selectedValues.filter(val =>
        (area.options || []).some(opt => opt.fieldValue === val)
      );
      if (validValues.length !== selectedValues.length) {
        area.selectedValue = [];
      }
    });
  }

  override onResetClicked = () => {
    const areaField = this.filterSchema.FieldsArray.find(x => x.fieldName === FieldNames['Area']);
    if (areaField) {
      areaField['displayName'] = 'Area / Region';
      areaField['options'] = [...DropdownOptions[FieldNames['Area']],...this.regions];
    }
    this.filterSchema.FieldsArray = this.filterSchema.FieldsArray.map((field) => ({ ...field, selectedValue: field['defaultValue'] ? field['defaultValue'] : '' }));
    this.dataTableSchema = {
      ...this.dataTableSchema,
      inputFilter: '',
      dataSource: []
    };
    this.resetSorting();
    this.checkForAnyEmptyFields();
  };

}